import { useState, useEffect } from 'react';
import { fetchTournaments, fetchEvents, submitTournamentInquiry } from '@/lib/api';
import { Trophy, Calendar, Clock, Users, Send } from 'lucide-react';
import SEO from '@/components/SEO';

export default function Tournaments() {
  const [tournaments, setTournaments] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', sport: '', message: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTournaments().then(setTournaments).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      await submitTournamentInquiry(form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', sport: '', message: '' });
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Failed to submit');
    }
  };

  return (
    <div data-testid="tournaments-page" className="pt-[68px]">
      <SEO title="Tournaments" description="Compete in box cricket, pickleball, and futsal tournaments at Propel Sports NC. Register your team today!" path="/tournaments" />
      <section className="relative py-24 bg-[#0B1D3A] overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(ellipse 600px 400px at 50% 50%, rgba(242,101,34,0.25) 0%, transparent 70%)'
        }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Tournaments</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">Compete in our upcoming tournaments across multiple sports</p>
        </div>
      </section>

      {/* Events */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-8 text-center">Upcoming Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {events.map(ev => (
              <div key={ev.id} data-testid={`event-${ev.id}`} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-[#F26522]/30 hover:shadow-lg transition-all">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={16} className="text-[#F26522]" />
                  <span className="text-[#F26522] text-xs font-semibold uppercase tracking-wider">{ev.status === 'coming_soon' ? 'Coming Soon' : ev.status}</span>
                </div>
                <h3 className="text-lg font-bold text-[#0B1D3A] mb-1">{ev.name}</h3>
                <p className="text-gray-500 text-sm mb-3">{ev.sport}</p>
                <p className="text-gray-400 text-xs">{ev.format}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0B1D3A] mb-8 text-center">Tournament Schedule</h2>
          <div className="space-y-4">
            {tournaments.map(t => (
              <div key={t.id} data-testid={`tournament-${t.id}`} className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#F26522]/30 hover:shadow-lg transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#0B1D3A] mb-1">{t.name}</h3>
                    <p className="text-gray-500 text-sm mb-2">{t.description}</p>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1"><Calendar size={14} className="text-[#F26522]" /> Fall 2026</span>
                      <span className="flex items-center gap-1"><Users size={14} className="text-[#F26522]" /> {t.format}</span>
                    </div>
                  </div>
                  <span className="shrink-0 bg-[#F26522]/10 text-[#F26522] px-6 py-3 rounded-lg font-semibold text-sm">
                    Coming Soon
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-20 bg-[#0B1D3A]">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">Tournament Inquiry</h2>
          <p className="text-gray-400 text-center mb-8">Interested in hosting or joining a tournament? Let us know!</p>
          {sent ? (
            <div data-testid="tournament-inquiry-success" className="bg-green-500/10 border border-green-500/30 rounded-2xl p-8 text-center">
              <p className="text-green-400 font-semibold">Inquiry submitted! We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="tournament-inquiry-form" className="space-y-4">
              {err && <p className="text-red-400 text-sm">{err}</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="Your Name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  data-testid="inquiry-name" className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-[#F26522]/50" />
                <input type="email" placeholder="Email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                  data-testid="inquiry-email" className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-[#F26522]/50" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  data-testid="inquiry-phone" className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-[#F26522]/50" />
                <select value={form.sport} onChange={e => setForm({...form, sport: e.target.value})} required
                  data-testid="inquiry-sport" className="bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-[#F26522]/50 [&>option]:bg-[#0B1D3A]">
                  <option value="">Select Sport</option>
                  <option value="Box Cricket">Box Cricket</option>
                  <option value="Pickleball">Pickleball</option>
                  <option value="Futsal">Futsal</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <textarea placeholder="Your Message" rows={4} required value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                data-testid="inquiry-message" className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-sm placeholder-gray-400 focus:outline-none focus:border-[#F26522]/50 resize-none" />
              <button type="submit" data-testid="inquiry-submit" className="w-full flex items-center justify-center gap-2 bg-[#F26522] hover:bg-[#D9551A] text-white px-6 py-3 rounded-lg font-semibold transition-all">
                <Send size={16} /> Submit Inquiry
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
